import { useEffect, useMemo, useRef, useState } from "react";

import CasosAbiertosModal from "../components/CasosAbiertosModal";

import { useTranslation } from "react-i18next";

import { Button, Input, Pagination, SectionHeader } from "../../../components/ui";
import CentinelaWatchPill from "../components/CentinelaWatchPill";
import RefreshSectionButton from "../components/RefreshSectionButton";
import UnitCallModal from "../components/UnitCallModal";
import UnitCustodyModal from "../components/UnitCustodyModal";
import TabLoader from "../components/TabLoader";
import UnitWatchCard from "../components/UnitWatchCard";
import UnitWatchModal from "../components/UnitWatchModal";
import { useWatchedFleet } from "../hooks/useWatchedFleet";
import { useVehiclesSocketStore } from "../../../store/admin/vehiclesSocketStore";
import { WATCH_STATUS_CARD } from "../theme";
import { WATCH_STATUSES, type WatchStatus } from "../types";

const ITEMS_PER_PAGE = 6;

type StatusFilter = WatchStatus | "all";

const FILTERS: StatusFilter[] = ["all", ...WATCH_STATUSES];

export default function UnidadesSection() {
  const { t } = useTranslation("common");
  const { refs, watchedUnits, breakdown, isLoading, isRefreshing, refresh } = useWatchedFleet();
  const requestAddressForDevice = useVehiclesSocketStore(
    (state) => state.requestAddressForDevice,
  );
  const requestedAddresses = useRef<Set<string>>(new Set());

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [page, setPage] = useState(1);
  const [detailUnitId, setDetailUnitId] = useState<string | null>(null);
  /** La unidad desde la que se abrió el modal de casos, para ponerla arriba. */
  const [casosDeDevice, setCasosDeDevice] = useState<string | null>(null);
  const [callUnitId, setCallUnitId] = useState<string | null>(null);
  const [custodyUnitId, setCustodyUnitId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return watchedUnits.filter(({ unit, watch }) => {
      const matchesStatus = statusFilter === "all" || watch.status === statusFilter;
      const matchesTerm =
        term.length === 0 ||
        unit.name.toLowerCase().includes(term) ||
        (unit.plate ?? "").toLowerCase().includes(term) ||
        (unit.driver?.name ?? "").toLowerCase().includes(term);
      return matchesStatus && matchesTerm;
    });
  }, [watchedUnits, search, statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const totalPages = Math.max(Math.ceil(filtered.length / ITEMS_PER_PAGE), 1);
  const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const visibleDeviceIds = paginated
    .flatMap(({ unit }) => (unit.devices ?? []).map((device) => device.id))
    .join(",");

  useEffect(() => {
    if (!visibleDeviceIds) return;
    visibleDeviceIds.split(",").forEach((deviceId) => {
      if (!deviceId || requestedAddresses.current.has(deviceId)) return;
      requestedAddresses.current.add(deviceId);
      requestAddressForDevice(deviceId);
    });
  }, [visibleDeviceIds, requestAddressForDevice]);

  const countOfFilter = (filter: StatusFilter) =>
    filter === "all" ? watchedUnits.length : breakdown[filter];

  const activeUnits = watchedUnits.length - breakdown.offline;
  const alertUnits = breakdown.alert;
  const uncustodiedUnits = breakdown.sin_custodia;

  const detailUnit = watchedUnits.find(({ unit }) => unit.id === detailUnitId) ?? null;
  const callUnit = watchedUnits.find(({ unit }) => unit.id === callUnitId) ?? null;
  const custodyUnit = watchedUnits.find(({ unit }) => unit.id === custodyUnitId) ?? null;

  const operatorNameOf = (name: string | undefined) => name ?? t("monitoreoIa.units.noDriver");

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <TabLoader label={t("monitoreoIa.units.loadingTab")} />
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col">
      <SectionHeader
        eyebrow={<CentinelaWatchPill />}
        title={t("monitoreoIa.units.title")}
        subtitle={t("monitoreoIa.units.subtitle", { count: refs.length })}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <RefreshSectionButton onRefresh={refresh} loading={isRefreshing} />
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-subtle px-3 py-1.5 text-xs font-medium text-content-secondary">
              <span className="h-2 w-2 rounded-full bg-success" />
              {t("monitoreoIa.units.activeCount", { count: activeUnits })}
            </span>
            {uncustodiedUnits > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-lg border border-info/30 bg-info/10 px-3 py-1.5 text-xs font-semibold text-info">
                <i className="ri-eye-off-line text-[13px]" />
                {t("monitoreoIa.units.uncustodiedCount", { count: uncustodiedUnits })}
              </span>
            )}
            {alertUnits > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-lg border border-error/30 bg-error/10 px-3 py-1.5 text-xs font-semibold text-error">
                <span className="h-2 w-2 rounded-full bg-error" />
                {t("monitoreoIa.units.atRiskCount", { count: alertUnits })}
              </span>
            )}
          </div>
        }
        filters={
          <div className="flex w-full flex-col gap-3">
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("monitoreoIa.units.searchPlaceholder")}
              icon={<i className="ri-search-line" />}
              containerClassName="w-full"
            />

            <div className="flex flex-wrap gap-2">
              {FILTERS.map((filter) => {
                const isActive = statusFilter === filter;
                const dot = filter === "all" ? null : WATCH_STATUS_CARD[filter].dot;

                return (
                  <button
                    key={filter}
                    type="button"
                    onClick={() => setStatusFilter(filter)}
                    className={`flex h-9 items-center gap-2 whitespace-nowrap rounded-lg border px-3.5 max-sm:h-10 text-[12.5px] font-semibold transition-colors ${
                      isActive
                        ? "border-primary-500 bg-primary-500 text-on-primary"
                        : "border-line bg-card text-content-secondary hover:border-primary-500/40 hover:text-content"
                    }`}
                  >
                    {dot && !isActive && <span className={`h-2 w-2 shrink-0 rounded-full ${dot}`} />}
                    {filter === "all"
                      ? t("monitoreoIa.units.allStatuses")
                      : t(`monitoreoIa.status.${filter}`)}
                    <span
                      className={`min-w-[20px] rounded-full px-1.5 py-0.5 text-center text-[10.5px] max-sm:text-[11px] font-bold ${
                        isActive ? "bg-black/15" : "bg-subtle text-content-secondary"
                      }`}
                    >
                      {countOfFilter(filter)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        }
      />

      <div className="flex-1 overflow-y-auto p-3 sm:p-6">
        <div className="mx-auto w-full max-w-6xl space-y-4">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-line bg-subtle/50 py-16">
              <i className="ri-truck-line mb-3 text-4xl text-content-faint" />
              <p className="text-sm text-content-secondary">
                {search
                  ? t("monitoreoIa.units.noResults", { term: search })
                  : t("monitoreoIa.units.emptyDescription")}
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="mt-3"
                onClick={() => {
                  setSearch("");
                  setStatusFilter("all");
                }}
              >
                {t("monitoreoIa.units.showAll")}
              </Button>
            </div>
          ) : (
            <>
              <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
                {paginated.map(({ unit, watch }) => (
                  <UnitWatchCard
                    key={unit.id}
                    unit={unit}
                    watch={watch}
                    onOpen={() => setDetailUnitId(unit.id)}
                    onCall={() => setCallUnitId(unit.id)}
                    onCustody={() => setCustodyUnitId(unit.id)}
                    /* Abre el modal de casos abiertos con el de esta unidad
                       arriba. No filtra a uno solo: quien llega desde una
                       unidad concreta casi siempre quiere ese caso, pero si de
                       paso hay otros tres esperando, ocultárselos no le hace
                       ningún favor. */
                    onResolverCaso={() => setCasosDeDevice(watch.deviceId)}
                  />
                ))}
              </div>

              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </>
          )}
        </div>
      </div>

      {detailUnit ? (
        <UnitWatchModal
          unit={detailUnit.unit}
          watch={detailUnit.watch}
          deviceId={detailUnit.watch.deviceId}
          onClose={() => setDetailUnitId(null)}
        />
      ) : null}

      {callUnit ? (
        <UnitCallModal
          unit={callUnit.unit}
          deviceId={callUnit.watch.deviceId}
          operatorName={operatorNameOf(callUnit.unit.driver?.name)}
          hasOperatorPhone={callUnit.watch.hasOperatorPhone}
          onClose={() => setCallUnitId(null)}
        />
      ) : null}

      {custodyUnit ? (
        <UnitCustodyModal
          unit={custodyUnit.unit}
          deviceId={custodyUnit.watch.deviceId}
          operatorName={operatorNameOf(custodyUnit.unit.driver?.name)}
          underCustody={custodyUnit.watch.underCustody}
          onClose={() => setCustodyUnitId(null)}
        />
      ) : null}

      {casosDeDevice ? (
        <CasosAbiertosModal
          deviceIdPrioritario={casosDeDevice}
          onClose={() => setCasosDeDevice(null)}
        />
      ) : null}
    </div>
  );
}
